import React, { useEffect, useRef, useState } from 'react';
import { Text, Separator, Icons, useTheme, Box } from '@ublend-npm/aulaui-core';
import { withRouter } from 'react-router';
import { useMediaQuery } from '@mui/material';
import { useLDClient } from 'launchdarkly-react-client-sdk';

const ALL_MENU_ITEMS = [
  {
    sectionName: 'User Settings',
    items: [
      {
        icon: <Icons.PermIdentity color="inherit" />,
        text: 'Account Details',
        path: '/account-details',
        isSelected: /profile\/account-details\/?$/,
        idx: 1,
      },
    ],
  },
  {
    sectionName: 'App Settings',
    items: [
      {
        icon: <Icons.NotificationsNone color="inherit" />,
        text: 'Notifications',
        path: '/notifications',
        isSelected: /profile\/notifications\/?$/,
        idx: 2,
      },
      {
        icon: <Icons.FormatColorFill color="inherit" />,
        text: 'Appearance',
        path: '/appearance',
        isSelected: /profile\/appearance\/?$/,
        idx: 4,
        featureFlag: 'WebAppFtAppearance202505', // Only show if feature flag is enabled
      },
    ],
  },
];

const flattenMenuItems = (menuItems: any[]) => {
  return menuItems.flatMap((section) =>
    section.items.map((item) => ({
      ...item,
      sectionName: section.sectionName,
    }))
  );
};

const LeftSideMenu = ({ router, location, route }: any) => {
  const { palette, breakpoints } = useTheme();
  const endPoint = location?.pathname?.split('/').pop();
  const isDesktop = useMediaQuery(breakpoints.up('md'));
  const AppBarComponent = route?.userBar;
  const ldClient = useLDClient();
  const flags = ldClient?.allFlags();

  let flatIndex = 0;

  // Filter menu items based on feature flags
  const MENU_ITEMS = ALL_MENU_ITEMS.map((section) => ({
    ...section,
    items: section?.items.filter(
      (item) => !item?.featureFlag || flags?.[item?.featureFlag]
    ),
  })).filter((section) => section.items.length > 0);

  const flatMenu = flattenMenuItems(MENU_ITEMS);
  const currentIdx = flatMenu.findIndex((item) =>
    item.isSelected.test(location.pathname)
  );
  const [selectedIdx, setSelectedIdx] = useState<number>(
    currentIdx >= 0 ? currentIdx : 0
  );
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Accessible via Keyboard Navigation (for Tab)
  useEffect(() => {
    if (itemRefs.current[selectedIdx]) {
      // eslint-disable-next-line no-unused-expressions
      itemRefs.current[selectedIdx]?.focus();
    }
  }, [selectedIdx]);

  useEffect(() => {
    if (endPoint === 'profile' && isDesktop) {
      router.replace(`${location?.pathname}/account-details`);
    }
  }, [isDesktop]);

  const handleOnClickNavigate = (pathname: string, idx: number) => () => {
    const url = location?.pathname?.replace(
      /\/profile\/.*$/,
      `/profile${pathname}`
    );
    setSelectedIdx(idx);
    if (isDesktop) {
      router.replace(url);
    } else {
      router.push(location?.pathname + pathname);
    }
  };

  const Title = ({ text }: any) => (
    <Text justifyItems="center" fontSize="14px" fontWeight="550" py={1}>
      {text}
    </Text>
  );

  return (
    <Box
      padding={isDesktop ? '2.5rem 0 2.5rem 1.5rem' : '1.5rem 1rem'}
      marginTop={0}
      width="100%"
      height="100vh"
      bgcolor={palette.background.default}
      id="content-container"
      component="ul"
      role="tablist"
    >
      {AppBarComponent && <AppBarComponent />}
      {MENU_ITEMS.map((section) => (
        <React.Fragment key={section.sectionName}>
          <Title text={section.sectionName} />
          {section.items.map((item) => {
            const idx = flatIndex;
            const flag = item.isSelected.test(location.pathname);

            const refCallback = (el) => {
              itemRefs.current[idx] = el;
            };

            flatIndex += 1;

            return (
              <Box
                ref={refCallback}
                onClick={handleOnClickNavigate(item.path, idx)}
                tabIndex={0}
                aria-selected={flag}
                component="button" // Accessible via Keyboard Navigation (for Tab)
                color="inherit"
                role="tab"
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: flag
                    ? palette.action.selected
                    : 'transparent',
                  ...(isDesktop && {
                    // hover effect is only for Desktop
                    '&:hover': {
                      backgroundColor: flag
                        ? palette.action.selected
                        : palette.action.hover,
                    },
                  }),
                  border: 'none',
                  outline: flag ? 'none' : undefined, // Accessible via Keyboard Navigation (for Tab)
                  transition: 'background-color 0.2s ease, box-shadow 0.2s',
                }}
              >
                <Box display="flex" gap="0.75rem" alignItems="center">
                  {item.icon}
                  <Text fontSize="16px" color="inherit">
                    {item.text}
                  </Text>
                </Box>
                {!isDesktop && (
                  <Icons.ArrowForwardIos
                    sx={{ height: '16px', width: '16px' }}
                  />
                )}
              </Box>
            );
          })}
          <Separator sx={{ mb: '24px', mt: '8px' }} />
        </React.Fragment>
      ))}
    </Box>
  );
};

export default withRouter(LeftSideMenu);
